import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { keyBy, merge } from 'lodash';

import WDT_CLASSROOM_ERRORS_BY_STUDENTS_QUERY from 'graph/reports/wdt-classroom-errors-by-students-report-query';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import BarChart from 'components/@common/Charts/BarChart';
import ErrorTypePicker from '../../common/ErrorTypePicker';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { CharacterErrorType, ModuleType, ReportEntityType } from '../../../../types';
import { getQueryVariables } from '../../../../utils/report-param-utils';
import { PrintViewContext } from '../../../../providers/PrintViewProvider';
import { sortAlphabeticallyByKeys } from 'utilities/sort-util';
import { WdtErrorData, WdtStudentErrorData } from 'types/schema-generated-types';
import {
  BAR_CHART_COMMON_OPTIONS,
  CHARACTER_ERROR_COLORS
} from '../../../..//utils/report-wdt-utils';

interface ErrorAnalysisForCharactersChartViewProps {
  module: ModuleType;
}

const ErrorAnalysisForCharactersChartView: React.FC<ErrorAnalysisForCharactersChartViewProps> = ({
  module
}) => {
  const { fullChartWidth } = useContext(PrintViewContext);
  const [{ dateRange, entityType, entityId }, setQuery] = useReportQueryParams();
  const [fetchReport, { data, loading }] = useLazyQuery(WDT_CLASSROOM_ERRORS_BY_STUDENTS_QUERY);
  const { t } = useTranslation('Reports');
  const [errorTypeFilter, setErrorTypeFilter] = useState<any>();

  const reportData: WdtStudentErrorData[] =
    data?.wdtClassroomErrorsByStudentsReport?.studentErrors ?? [];

  const filterErrorType = (error: WdtErrorData): boolean =>
    !errorTypeFilter || error.type === errorTypeFilter;

  const sortByStudent = (a: WdtStudentErrorData, b: WdtStudentErrorData): number =>
    sortAlphabeticallyByKeys(['student.firstName', 'student.lastName'], a, b);

  const {
    ids,
    students,
    missedStartingPointSeries,
    startedWrongWaySeries,
    outOfBoundsSeries,
    liftedFingerSeries,
    reversedDirectionSeries
  } = reportData
    .slice()
    .sort(sortByStudent)
    .reduce(
      (acc, { student, errors }) => {
        if (!student) return acc;

        const errorsByType = keyBy(errors.filter(filterErrorType), 'type');

        acc.ids.add(student.id);
        acc.students.push(`${student.firstName} ${student.lastName}`);
        acc.outOfBoundsSeries.push(errorsByType[CharacterErrorType.OUT_OF_BOUNDS]?.count ?? 0);
        acc.liftedFingerSeries.push(errorsByType[CharacterErrorType.LIFTED_FINGER]?.count ?? 0);
        acc.missedStartingPointSeries.push(
          errorsByType[CharacterErrorType.MISSED_STARTING_POINT]?.count ?? 0
        );
        acc.startedWrongWaySeries.push(
          errorsByType[CharacterErrorType.STARTED_WRONG_WAY]?.count ?? 0
        );
        acc.reversedDirectionSeries.push(
          errorsByType[CharacterErrorType.REVERSED_DIRECTION]?.count ?? 0
        );
        return acc;
      },
      {
        ids: new Set(),
        students: [] as string[],
        missedStartingPointSeries: [] as number[],
        startedWrongWaySeries: [] as number[],
        outOfBoundsSeries: [] as number[],
        liftedFingerSeries: [] as number[],
        reversedDirectionSeries: [] as number[]
      }
    );

  const series = [
    {
      name: t(CharacterErrorType.MISSED_STARTING_POINT, { ns: 'CharacterErrorType' }),
      data: missedStartingPointSeries
    },
    {
      name: t(CharacterErrorType.STARTED_WRONG_WAY, { ns: 'CharacterErrorType' }),
      data: startedWrongWaySeries
    },
    {
      name: t(CharacterErrorType.OUT_OF_BOUNDS, { ns: 'CharacterErrorType' }),
      data: outOfBoundsSeries
    },
    {
      name: t(CharacterErrorType.LIFTED_FINGER, { ns: 'CharacterErrorType' }),
      data: liftedFingerSeries
    },
    {
      name: t(CharacterErrorType.REVERSED_DIRECTION, { ns: 'CharacterErrorType' }),
      data: reversedDirectionSeries
    }
  ];

  const handleChartClick = (index: number): void => {
    setQuery({ entityType: ReportEntityType.STUDENT, entityId: [...ids][index] }, 'pushIn');
  };

  const options = merge({}, BAR_CHART_COMMON_OPTIONS, {
    colors: CHARACTER_ERROR_COLORS,
    yaxis: {
      forceNiceScale: false,
      max: Math.max(...series.flatMap(item => item.data)),
      title: { text: 'Error Count' }
    },
    xaxis: { categories: students },
    stroke: { show: true, colors: ['transparent'], width: 10 }
  });

  useEffect(() => {
    fetchReport({
      variables: merge(getQueryVariables({ entityId, entityType, dateRange }), {
        where: {
          module
        }
      })
    });
  }, [fetchReport, entityId, entityType, dateRange, module]);

  const cardTitle = (
    <Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
      <Box flex={1}>
        <Typography variant="h4">{t(`Error for ${t(module, { ns: 'ModuleType' })}`)}</Typography>
      </Box>
      <Typography component="label">{t(`Error Type`)}</Typography>
      <Box ml={1} width="200px">
        <ErrorTypePicker
          value={errorTypeFilter}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            setErrorTypeFilter(event.target.value)
          }
        />
      </Box>
    </Box>
  );

  return (
    <ReportCard title={cardTitle} loading={loading} reportData={reportData}>
      <BarChart
        height={250}
        width={fullChartWidth}
        series={series}
        options={options}
        onClick={handleChartClick}
      />
    </ReportCard>
  );
};

export default ErrorAnalysisForCharactersChartView;
